import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ModalController } from "@ionic/angular/standalone";
import { IonIcon } from "@ionic/angular/standalone";
import { Store } from '@ngrx/store';
import { AccountType, DateUtil, DisplayService } from "@qid/core";
import { SexyModule } from '@qid/sexy';
import { AuthSelectors } from '../../../authentication';
import { Share } from "../../model/Share";
import { ShareService } from "../../services/share.service";
import { CheckoutModalComponent } from '../checkout-modal/checkout-modal.component';

@Component({
  selector: 'share-checkin-view',
  templateUrl: './share-checkin-view.component.html',
  styleUrls: ['./share-checkin-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    IonIcon
  ]
})
export class ShareCheckinViewComponent implements OnInit {

  @Input() share: Share
  @Output() shareChange: EventEmitter<Share> = new EventEmitter<Share>()

  stayDuration: number = null;

  // ponytail: account-type gate only; role matrix if check-in permissions land later
  private readonly activeAccountType = toSignal(
    inject(Store).select(AuthSelectors.selectActiveAccountType),
    { initialValue: null },
  );
  readonly isOrgUser = computed(() => this.activeAccountType() === AccountType.organisation);

  constructor(private modalController: ModalController,
    private shareService: ShareService,
    private displayService: DisplayService
  ) {
  }

  ngOnInit() {
    this.stayDuration = this.getStayDuration();
  }

  async onEditCheckInClicked() {
    if (!this.isOrgUser()) return;

    const modal = await this.modalController.create({
      component: CheckoutModalComponent,
      componentProps: {
        share: this.share,
        editMode: true,
        saveCheckIn: (checkInTime: Date) => this.persistCheckInTime(checkInTime)
      },
      animated: true
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  private async persistCheckInTime(checkInTime: Date): Promise<boolean> {
    const res = await this.shareService.update(this.share._id, {
      checkInData: {
        checkInTime,
        checkOutTime: this.share?.checkInData?.checkOutTime,
        remark: this.share?.checkInData?.remark
      },
      updateRemark: 'Mark as Checked-in',
      changeLastUpdateTime: false
    });
    if (res?.data) {
      this.share = res.data;
      this.shareChange.emit(res.data);
      return true;
    }
    return false;
  }

  async onCheckOutClicked() {
    if (!this.isOrgUser()) return;

    const modal = await this.modalController.create({
      component: CheckoutModalComponent,
      componentProps: {
        share: this.share
      },
      animated: true
    });

    await modal.present();

    const modalResult = await modal.onDidDismiss();

    if (modalResult.data?.success) {
      const { checkInTime, checkOutTime, remark, accommodationCharges, submitToTimes } = modalResult.data.data;
      const res = await this.checkout(checkInTime, checkOutTime, remark, submitToTimes);
      if (!res) {
        return false;
      }
    }
  }


  async checkout(checkInTime?: Date, checkOutTime?: Date, remark?: string, submitToTimes?: boolean) {
    // Use provided checkInTime, fallback to existing share data if not provided
    const finalCheckInTime = checkInTime || this.share?.checkInData?.checkInTime || this?.share?.createTime

    if (new Date(finalCheckInTime).getTime() > new Date(checkOutTime).getTime()) {
      this.displayService.toast({
        message: 'Check out cannot happen before check in'
      })
      return false;
    }


    const res = await this.shareService.update(this.share._id,
      {
        checkInData: {
          checkInTime: finalCheckInTime,
          checkOutTime: checkOutTime,
          remark: remark
        },
        updateRemark: 'Check out',
        changeLastUpdateTime: false
      })

    if (res && res.data) {
      this.shareChange.emit(res.data)
      return true
    }

  }

  getStayDuration() {
    if (!this.share?.checkInData?.checkInTime || !this.share?.checkInData?.checkOutTime) {
      return null
    }
    const diff = DateUtil.getTimeDifferenceInDays(
      new Date(this.share?.checkInData?.checkInTime),
      new Date(this.share?.checkInData?.checkOutTime))

    return +diff.toFixed(0)
  }
}
